import React, { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { InputField, InputGroup } from '../shared/InputGroup.jsx';

/**
 * "Describe a state" above the Controls (config `ask`, dev server only):
 * a sentence such as "small red button, disabled, in dark mode" sets the
 * story's closed-set controls and the toolbar globals (src/node/ask-args.js).
 *
 * No summary line: the answer shows where it lands. Changed rows (and a
 * changed global's switch in the top bar) glow; rows the sentence asked for
 * that already had that value glow faintly; when nothing applies, the field
 * shakes. A visually hidden live region says the same for screen readers.
 */
export function DescribeState({ storyId, story, globals, onArgs, onGlobals }) {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [announce, setAnnounce] = useState('');
  const rootRef = useRef(null);

  // A different story starts clean.
  useEffect(() => {
    setQuery('');
    setAnnounce('');
  }, [storyId]);

  async function submit() {
    const q = query.trim();
    if (!q || busy) return;
    setBusy(true);
    try {
      const res = await fetch('/__edl/ask-args', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q, title: story.title, story: story.name, argTypes: story.argTypes, args: story.args, globals }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
      if (Object.keys(data.args).length) onArgs(data.args);
      if (Object.keys(data.globals).length) onGlobals(data.globals);
      // Wait for the rows to show their new values, then point at them.
      await nextFrame();
      await nextFrame();
      const flashed = [
        ...data.changes.map((c) => ({ ...c, strong: true })),
        ...(data.matched ?? []).map((c) => ({ ...c, strong: false })),
      ];
      if (flashed.length) point(flashed);
      else shake(rootRef.current?.querySelector('.edl-input-field__box'));
      setAnnounce(describe(data));
    } catch (error) {
      shake(rootRef.current?.querySelector('.edl-input-field__box'));
      setAnnounce(`Could not set the controls: ${error.message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="edl-describe" ref={rootRef} aria-busy={busy}>
      {/* Compact, like the Controls rows it sets. */}
      <InputGroup size="compact">
        <InputField
          label="Describe a state"
          icon={Sparkles}
          value={query}
          onChange={setQuery}
          placeholder="e.g. small, disabled, in dark mode"
          className={busy ? 'is-busy' : undefined}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
        />
      </InputGroup>
      <p className="edl-visually-hidden" aria-live="polite">
        {announce}
      </p>
    </div>
  );
}

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const nextFrame = () => new Promise((r) => requestAnimationFrame(r));

/** Glow each control the answer touched, in panel order, after scrolling the first into view. */
function point(items) {
  const rows = items
    .map((c) => ({ c, el: c.global ? document.querySelector(`[data-global="${CSS.escape(c.key)}"]`) : document.querySelector(`.edl-controls [data-arg="${CSS.escape(c.key)}"]`) }))
    .filter((r) => r.el);
  const inPanel = rows.filter((r) => !r.c.global).sort((a, b) => (a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
  inPanel[0]?.el.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
  inPanel.forEach(({ c, el }, i) => {
    // In fast, held long enough to be found, then out.
    const peak = c.strong ? 1 : 0.45;
    el.animate([{ opacity: 0 }, { opacity: peak, offset: 0.12 }, { opacity: peak, offset: 0.55 }, { opacity: 0 }], {
      duration: 1800,
      delay: reduced() ? 0 : 120 + i * 70,
      easing: 'cubic-bezier(0.2, 0, 0, 1)',
      pseudoElement: '::before',
    });
  });
  // A global's switch lives in the top bar: a ring around it, not a fill.
  for (const { c, el } of rows.filter((r) => r.c.global)) {
    const ring = getComputedStyle(el).getPropertyValue('--edl-fg').trim() || 'currentColor';
    el.animate(
      [
        { outline: '2px solid transparent', outlineOffset: '3px' },
        { outline: `2px solid color-mix(in srgb, ${ring} ${c.strong ? 80 : 40}%, transparent)`, outlineOffset: '3px', offset: 0.12 },
        { outline: `2px solid color-mix(in srgb, ${ring} ${c.strong ? 80 : 40}%, transparent)`, outlineOffset: '3px', offset: 0.55 },
        { outline: '2px solid transparent', outlineOffset: '3px' },
      ],
      { duration: 1800, delay: reduced() ? 0 : 120, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
    );
  }
}

/** "Nothing here for that": a short horizontal shake (a border flash with reduced motion). */
function shake(el) {
  if (!el) return;
  if (reduced()) {
    el.animate([{ opacity: 1 }, { opacity: 0.5 }, { opacity: 1 }], { duration: 400 });
    return;
  }
  el.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-4px)' }, { transform: 'translateX(4px)' }, { transform: 'translateX(-2px)' }, { transform: 'translateX(0)' }], {
    duration: 320,
    easing: 'cubic-bezier(0.2, 0, 0, 1)',
  });
}

function describe({ changes, matched = [] }) {
  const v = (x) => (x === true ? 'on' : x === false ? 'off' : x === '' || x == null ? 'none' : String(x));
  const parts = [];
  if (changes.length) parts.push(`Changed ${changes.map((c) => `${c.key} to ${v(c.to)}`).join(', ')}.`);
  if (matched.length) parts.push(`Already set: ${matched.map((c) => `${c.key} ${v(c.value)}`).join(', ')}.`);
  return parts.join(' ') || 'Nothing in the controls matches that.';
}
